import * as R from 'ramda'
import { computed } from 'vue'

import { TeamSide } from '@collector/shared-domain'
import {
  Coordinates,
  getCoordinateXForTeamSide,
} from '@collector/shared-ui-domain'
import { IncidentId } from '@collector/sportsapi-client-legacy'
import { Incident } from '@collector/sportsapi-types'
import {
  useEventSettings,
  useIncidents,
  useLastIncident,
  useTeamSides,
} from '@desktop/views/Relation/composables'
import { useEventIncidentsQueue } from '@desktop/views/Relation/Sports/composables'
import {
  injectFieldClickCoordinatesIncidentConfig,
  injectIncidentsRequirePointingPositionConfig,
  injectIncidentsTeamPossessionConfig,
} from '@desktop/views/Relation/Sports/provide-inject'

export function useFieldCoordinatesIncident(): {
  sendFieldCoordinatesIncident: (
    event: MouseEvent,
    coordinates: Coordinates,
  ) => void
} {
  // inject
  const { getIncidentIdForCoordinateX } =
    injectIncidentsRequirePointingPositionConfig()
  const fieldClickCoordinatesIncidentConfig =
    injectFieldClickCoordinatesIncidentConfig()
  const incidentsTeamPossessionConfig = injectIncidentsTeamPossessionConfig()
  // composables
  const { eventSettings } = useEventSettings()
  const { lastNonDeletedIncident } = useLastIncident()
  const { incidents } = useIncidents()
  const { getIncidentTeamSide, getTeamSideParticipant } = useTeamSides()
  const eventIncidentsQueue = useEventIncidentsQueue()

  // computed
  const invertedIncidents = computed(() => R.reverse([...incidents.value]))

  // methods
  function getIncidentToUpdate(
    incident: Incident | undefined,
    incidentId: IncidentId,
  ): Incident | undefined {
    const isIncidentCreationForcedByConfig =
      fieldClickCoordinatesIncidentConfig &&
      fieldClickCoordinatesIncidentConfig.forceCreateIncidentIds.includes(
        incidentId,
      )

    if (isIncidentCreationForcedByConfig) return

    const incidentFromConfig =
      fieldClickCoordinatesIncidentConfig?.getIncidentToUpdateCoordinates?.[
        incidentId
      ]?.(invertedIncidents.value)
    if (incidentFromConfig) return incidentFromConfig

    if (!incident) return

    // new position in the same area as last incident
    const isEqualIncidentIdAsLastIncident = incidentId === incident.incident_id

    // new position was clicked by the same participant
    const isEqualParticipantAsLastIncident =
      getIncidentTeamSide(incident) === eventSettings.value.teamSidePossession

    return isEqualIncidentIdAsLastIncident && isEqualParticipantAsLastIncident
      ? incident
      : undefined
  }

  function sendFieldCoordinatesIncident(
    event: MouseEvent,
    { x, y }: Coordinates,
  ): void {
    if (!eventSettings.value.teamSidePossession) {
      return
    }

    const incidentId = getIncidentIdForCoordinateX(
      event,
      getCoordinateXForTeamSide(x, eventSettings.value.teamSidePossession),
      eventSettings.value.teamSidePossession,
    )

    if (!incidentId) {
      return
    }

    const payload = {
      incident_id: incidentId,
      x_pos: x,
      y_pos: y,
    }

    const incidentToUpdate = getIncidentToUpdate(
      lastNonDeletedIncident.value,
      incidentId,
    )

    if (
      incidentToUpdate &&
      !isLastIncidentTeamSideDifferentThanCurrent(incidentId)
    ) {
      eventIncidentsQueue.updateIncident(payload, incidentToUpdate)
    } else {
      const participantInPossession = getTeamSideParticipant(
        eventSettings.value.teamSidePossession,
      )
      if (!participantInPossession) return

      eventIncidentsQueue.addIncident({
        ...payload,
        participant_team_id: participantInPossession.id,
      })
    }
  }

  function isLastIncidentTeamSideDifferentThanCurrent(
    incidentId: IncidentId,
  ): boolean {
    let teamSidePossession = eventSettings.value.teamSidePossession
    let lastNonDeletedIncidentTeamSide: TeamSide | null = null
    if (lastNonDeletedIncident.value) {
      lastNonDeletedIncidentTeamSide = getIncidentTeamSide(
        lastNonDeletedIncident.value,
      )
    }

    const shouldSwapPossession =
      incidentsTeamPossessionConfig?.swapPossessionIncidents?.has(incidentId)
    if (shouldSwapPossession) {
      if (teamSidePossession === 'home') teamSidePossession = 'away'
      else if (teamSidePossession === 'away') teamSidePossession = 'home'
    }

    return lastNonDeletedIncidentTeamSide !== teamSidePossession
  }

  return {
    sendFieldCoordinatesIncident,
  }
}
